import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ArrowLeft, ExternalLink, History, Plus, Send, Square, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { AgentThinkingComic } from '@/webmcp/ui/AgentThinkingComic';
import { ChatReportCard } from './ChatReportCard';
import { ChatAccessModeSelect } from './ChatAccessModeSelect';
import { AgilePlanApprovalCard } from './AgilePlanApprovalCard';
import { ClientStatusApprovalCard } from './ClientStatusApprovalCard';
import { NewClientApprovalCard } from './NewClientApprovalCard';
import { isAgilePlanApproval, isClientStatusApproval, isNewClientApproval, type CentralChatMessage } from './types';
import { WriteApprovalCard } from './WriteApprovalCard';
import { TechnicalAuditProposalCard } from './TechnicalAuditProposalCard';
import { MarkdownContent } from './MarkdownContent';
import { awaitingLabel, hasSettledTrace } from './chatActivity';
import { EmjaeTrace } from './EmjaeTrace';
import { useCentralChatRuntime } from './centralChatRuntime';
import { offeredRoutePath } from './navigation';
import { presetsFor, welcomeFor } from './chatPagePresets';
import { EmjaeMascot } from './EmjaeMascot';
import {
  headerPose,
  replyHasText,
  useEmjaeMascotMotion,
} from './emjaeMascotMotion';

function tenantDomain(tenantId: string | null): string {
  if (!tenantId) return 'no workspace';
  return tenantId.replace(/^https?:\/\//, '').replace(/\/$/, '');
}

function relativeTime(iso?: string | null): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const mins = Math.floor((Date.now() - then) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

export interface CentralChatPanelBodyProps {
  /** Overrides the default `domain · context` subtitle line under "Emjae". */
  subtitle?: ReactNode;
  /** Chrome-specific buttons (collapse / close) rendered after the shared
   *  History / New / Full-chat cluster. */
  chromeActions?: ReactNode;
  /** Render the fetching / in-flight status as a banner at the TOP of the thread
   *  (the rail's contract) rather than only in the composer footer. */
  showStatusBanner?: boolean;
  /** Called when opening a report card should dismiss this surface (the bubble
   *  closes; the rail stays open side-by-side and leaves this undefined). */
  onLeave?: () => void;
}

/**
 * The shared Emjae panel interior — header, thinking comic, history/chat views
 * and composer — used by both the floating bubble and the right-side rail.
 *
 * It composes the runtime (`useCentralChatRuntime`) rather than taking it as a
 * prop, so each surface only supplies its own chrome (sizing, positioning, and
 * the collapse/close buttons). `view` state is internal, so a surface that
 * unmounts the body on collapse (the bubble) resets to the chat view for free.
 */
export function CentralChatPanelBody({
  subtitle,
  chromeActions,
  showStatusBanner = false,
  onLeave,
}: CentralChatPanelBodyProps) {
  const chat = useCentralChatRuntime();
  const scrollAnchorRef = useRef<HTMLDivElement | null>(null);
  const [view, setView] = useState<'chat' | 'history'>('chat');
  const motion = useEmjaeMascotMotion({
    isStreaming: chat.isStreaming,
    messages: chat.messages,
    bubbleOpen: chat.bubbleOpen,
    openNonce: chat.openNonce,
  });

  useEffect(() => {
    if (view !== 'chat') return;
    scrollAnchorRef.current?.scrollIntoView({ block: 'end' });
  }, [chat.messages, chat.isStreaming, view]);

  const showHistory = () => {
    chat.refreshConversations();
    setView('history');
  };

  const openFromHistory = (conversationId: string) => {
    chat.openConversation(conversationId);
    setView('chat');
  };

  const newChat = () => {
    chat.startNew();
    setView('chat');
  };

  // The one animated avatar: the last message, while it is an assistant reply being generated.
  const lastMessage = chat.messages.at(-1);
  const liveReplyId = chat.isStreaming && lastMessage?.role === 'assistant' ? lastMessage.id : null;
  // Then it celebrates once when the reply finished well (happy), and is the still image again.
  const happyReplyId = !chat.isStreaming && motion.happy ? motion.bounceId : null;
  const toolRunning = chat.toolActivity.some((entry) => entry.status === 'running');
  const livePose = (message: CentralChatMessage) =>
    headerPose({ isStreaming: true, toolRunning, replyStarted: replyHasText([message]), happy: false, wave: false });

  // A page with its own starter set shows it in place of the four generic buttons, and a click
  // sends it. Other pages keep the generic buttons, which fill the box.
  const pagePresets = presetsFor(chat.pathname, chat.clientName, chat.tenantId);
  const bannerText = chat.status || (chat.isStreaming ? "I'm working…" : '');
  const showBanner = showStatusBanner && Boolean(bannerText);

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <header className="flex h-14 shrink-0 items-center gap-3 border-b border-warm-border bg-paper px-3">
        <div className="min-w-0 flex-1">
          <div className="truncate font-display text-[17px] leading-none">Emjae</div>
          <div className="mt-1 truncate font-mono text-[10px] uppercase tracking-wider text-ink-3">
            {subtitle ?? `${tenantDomain(chat.tenantId)} · ${chat.contextLabel}`}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={view === 'history' ? () => setView('chat') : showHistory}
            title={view === 'history' ? 'Back to chat' : 'History'}
            aria-pressed={view === 'history'}
            className="grid size-8 place-items-center rounded-md text-ink-2 transition-colors hover:bg-paper-2 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral/40"
          >
            {view === 'history' ? <ArrowLeft className="size-4" /> : <History className="size-4" />}
          </button>
          <button
            type="button"
            onClick={newChat}
            title="New chat"
            className="grid size-8 place-items-center rounded-md text-ink-2 transition-colors hover:bg-paper-2 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral/40"
          >
            <Plus className="size-4" />
          </button>
          <button
            type="button"
            onClick={chat.openFullChat}
            title="Open full chat"
            className="grid size-8 place-items-center rounded-md text-ink-2 transition-colors hover:bg-paper-2 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral/40"
          >
            <ExternalLink className="size-4" />
          </button>
          {chromeActions}
        </div>
      </header>

      <AgentThinkingComic variant="expanded" />

      {view === 'history' ? (
        <div className="flex-1 overflow-y-auto px-3 py-3">
          <div className="mb-3 flex items-center justify-between">
            <span className="font-mono text-[10px] uppercase tracking-wider text-ink-3">
              Conversations
            </span>
            <button
              type="button"
              onClick={newChat}
              className="rounded-md border border-warm-border bg-paper px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-ink-2 transition-colors hover:border-warm-border-strong hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral/40"
            >
              New chat
            </button>
          </div>
          {chat.conversationLoading ? (
            <p className="px-1 py-6 text-center text-[13px] text-ink-3">Loading conversations...</p>
          ) : chat.conversations.length === 0 ? (
            <p className="px-1 py-6 text-center text-[13px] text-ink-3">No conversations yet</p>
          ) : (
            <ul className="flex flex-col gap-1">
              {chat.conversations.map((conversation) => (
                <li key={conversation.id} className="group flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => openFromHistory(conversation.id)}
                    className={cn(
                      'min-w-0 flex-1 rounded-md px-2 py-2 text-left transition-colors hover:bg-paper-2',
                      conversation.id === chat.activeConversationId && 'bg-paper-2',
                    )}
                  >
                    <span className="block truncate text-[13px] text-ink">
                      {conversation.title || 'Emjae chat'}
                    </span>
                    <span className="mt-0.5 block truncate font-mono text-[10px] uppercase tracking-wider text-ink-3">
                      {[
                        conversation.pageLabel,
                        relativeTime(conversation.last_message_at) ||
                          `${conversation.message_count} messages`,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => chat.deleteConversation(conversation.id)}
                    title="Delete conversation"
                    className="grid size-8 shrink-0 place-items-center rounded-md text-ink-3 opacity-0 transition-all hover:bg-destructive/10 hover:text-destructive focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-destructive/30 group-hover:opacity-100"
                  >
                    <Trash2 className="size-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          {/* Fetching / in-flight banner pinned to the TOP of the thread (rail
              contract). The composer footer still carries its own status line. */}
          {showBanner && (
            <div
              role="status"
              className="flex shrink-0 items-center gap-2 border-b border-warm-border bg-paper-2 px-3 py-1.5"
            >
              <span className="size-1.5 animate-pulse rounded-full bg-teal" aria-hidden="true" />
              <span className="truncate font-mono text-[10px] uppercase tracking-wider text-ink-2">
                {bannerText}
              </span>
            </div>
          )}
          <div className="flex-1 overflow-y-auto px-3 py-4">
            {chat.messages.length === 0 ? (
              <div className="space-y-4">
                <div className="flex justify-center">
                  <EmjaeMascot size="hero" motion={['float', motion.wiggle && 'wiggle']} />
                </div>
                <div>
                  <div className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-teal-dark">
                    <span className="size-1.5 rounded-full bg-teal" aria-hidden="true" />
                    ready
                  </div>
                  <h2 className="mt-2 font-display text-[24px] leading-tight text-ink">
                    Start from this page.
                  </h2>
                </div>
                <p data-testid="chat-welcome-line" className="text-[14px] leading-snug text-ink-2">
                  {welcomeFor(chat.pathname, chat.clientName)}
                </p>
                <div className="grid gap-2">
                  {pagePresets.length > 0
                    ? pagePresets.map((preset) => (
                        <button
                          key={preset.prompt}
                          type="button"
                          onClick={() => chat.submit(preset.prompt)}
                          disabled={!chat.tenantId || chat.isStreaming}
                          className="flex items-center gap-2 rounded-md border border-warm-border bg-paper px-3 py-2 text-left transition-colors hover:border-warm-border-strong hover:bg-paper-2 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <span className="text-[13px] text-ink">{preset.label}</span>
                        </button>
                      ))
                    : chat.modes.slice(0, 4).map((mode) => (
                        <button
                          key={mode.id}
                          type="button"
                          onClick={() => chat.handleMode(mode)}
                          disabled={!chat.tenantId || chat.isStreaming}
                          className="flex items-center gap-2 rounded-md border border-warm-border bg-paper px-3 py-2 text-left transition-colors hover:border-warm-border-strong hover:bg-paper-2 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <span className="font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3">
                            {mode.number}
                          </span>
                          <span className="text-[13px] text-ink">{mode.label}</span>
                        </button>
                      ))}
                </div>
              </div>
            ) : (
              <div role="log" aria-live="polite" className="flex flex-col gap-2">
                {chat.messages.map((message) => (
                  <article
                    key={message.id}
                    className={cn(
                      'mt-6 flex gap-2.5 first:mt-0',
                      message.role === 'user' ? 'flex-col items-end' : 'flex-row items-start',
                    )}
                  >
                    {message.role === 'assistant' && (
                      <EmjaeMascot
                        size="avatar"
                        // The reply being generated plays the sprite (thinking, talking, then happy once);
                        // every other avatar is the still image. The sprite is the motion, so no sway.
                        live={liveReplyId === message.id || happyReplyId === message.id}
                        pose={liveReplyId === message.id ? livePose(message) : happyReplyId === message.id ? 'happy' : 'idle'}
                        onAnimationEnd={() => {
                          motion.clearBounce();
                          motion.clearHappy();
                        }}
                      />
                    )}
                    <div
                      className={cn(
                        'text-[13px] leading-relaxed',
                        message.role === 'user'
                          ? 'max-w-7/8 rounded-lg bg-coral-light px-3 py-2 text-coral-dark'
                          : 'min-w-0 flex-1 text-ink',
                      )}
                    >
                      {message.role === 'assistant' ? (
                        <>
                          {chat.isStreaming && message.id === chat.messages.at(-1)?.id ? (
                            <div className="mb-1.5">
                              <EmjaeTrace
                                entries={chat.toolActivity}
                                startedAt={chat.turnStartedAt}
                                status={chat.status || "I'm reading…"}
                              />
                            </div>
                          ) : (
                            hasSettledTrace(message) && (
                              <div className="mb-1.5">
                                <EmjaeTrace
                                  entries={message.trace ?? []}
                                  startedAt={null}
                                  durationMs={message.traceDurationMs}
                                  outcome={message.traceOutcome}
                                  awaiting={message.awaiting}
                                />
                              </div>
                            )
                          )}
                          {message.content.trim() ? (
                            <MarkdownContent content={message.content} />
                          ) : chat.isStreaming && message.id === chat.messages.at(-1)?.id ? null : (
                            <span className="text-ink-3 italic">No response.</span>
                          )}
                        </>
                      ) : (
                        <span className="whitespace-pre-wrap">{message.content}</span>
                      )}
                      {message.role === 'assistant' &&
                        message.quickReplies?.length &&
                        message.id === chat.messages.at(-1)?.id &&
                        !chat.isStreaming && (
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {message.quickReplies.map((reply) => (
                              <button
                                key={reply}
                                type="button"
                                onClick={() => chat.submit(reply)}
                                disabled={!chat.tenantId}
                                className="rounded-full border border-warm-border bg-paper-2 px-2.5 py-1 text-[12px] text-ink transition-colors hover:border-coral hover:bg-coral-light hover:text-coral-dark disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral/40"
                              >
                                {reply}
                              </button>
                            ))}
                          </div>
                        )}
                      {message.role === 'assistant' &&
                        message.question &&
                        message.question.options.length > 0 &&
                        message.id === chat.messages.at(-1)?.id &&
                        !chat.isStreaming && (
                          <div className="mt-2 flex flex-wrap gap-1.5" role="group" aria-label="Choices">
                            {message.question.options.map((option) => (
                              <button
                                key={option.id}
                                type="button"
                                title={option.description}
                                onClick={() => chat.answerQuestion({ optionId: option.id })}
                                disabled={!chat.tenantId}
                                className="rounded-full border border-warm-border bg-paper-2 px-2.5 py-1 text-left text-[12px] text-ink transition-colors hover:border-coral hover:bg-coral-light hover:text-coral-dark disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral/40"
                              >
                                <span className="block">{option.label}</span>
                                {option.description && (
                                  <span className="block text-[11px] text-ink-3">{option.description}</span>
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                      {message.reportProposalId && (
                        <div className="mt-2">
                          <ChatReportCard
                            proposalId={message.reportProposalId}
                            tenantId={chat.tenantId}
                            onOpen={onLeave}
                          />
                        </div>
                      )}
                      {message.actions?.some((action) => offeredRoutePath(action)) && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {message.actions.map((action) => {
                            const path = offeredRoutePath(action);
                            if (!path) return null;
                            return (
                              <button
                                key={action.id}
                                type="button"
                                onClick={() => chat.sandboxOutput({ action: 'navigate', path })}
                                disabled={!chat.tenantId}
                                className="rounded-full border border-warm-border bg-paper-2 px-2.5 py-1 text-[12px] text-ink transition-colors hover:border-coral hover:bg-coral-light hover:text-coral-dark disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral/40"
                              >
                                {action.label}
                              </button>
                            );
                          })}
                        </div>
                      )}
                      {message.error && (
                        <div className="mt-2 rounded-md border border-destructive/20 bg-destructive/5 px-2 py-1 font-mono text-[10px] text-destructive">
                          {message.error}
                        </div>
                      )}
                      {message.taProposal && (
                        <div className="mt-2">
                          <TechnicalAuditProposalCard
                            key={message.taProposal.proposalId}
                            proposal={message.taProposal}
                            startPhase={message.taProposalRestored?.phase}
                            startReceipt={message.taProposalRestored?.receipt}
                          />
                        </div>
                      )}
                      {message.writeApproval ? (
                        (isClientStatusApproval(message.writeApproval) || isNewClientApproval(message.writeApproval)) &&
                        message.writeApproval.declineOnly ? (
                          // Nothing to approve: never a card. Core no longer sends one; an old stored park shows its notice as text.
                          message.writeApproval.notice ? (
                            <p className="mt-2 text-[12px] text-ink">{message.writeApproval.notice}</p>
                          ) : null
                        ) : isAgilePlanApproval(message.writeApproval) ? (
                          <AgilePlanApprovalCard
                            card={message.writeApproval}
                            busy={chat.isStreaming || message.settling === true}
                            results={message.agilePlanResults}
                            resultText={message.agilePlanResultText}
                            onApprove={() => chat.settleWriteApproval('approve')}
                            onDecline={() => chat.settleWriteApproval('cancel')}
                          />
                        ) : isClientStatusApproval(message.writeApproval) ? (
                          <ClientStatusApprovalCard
                            card={message.writeApproval}
                            busy={chat.isStreaming}
                            onApprove={() => chat.settleWriteApproval('approve')}
                            onDecline={() => chat.settleWriteApproval('cancel')}
                          />
                        ) : isNewClientApproval(message.writeApproval) ? (
                          <NewClientApprovalCard
                            card={message.writeApproval}
                            busy={chat.isStreaming}
                            onApprove={() => chat.settleWriteApproval('approve')}
                            onDecline={() => chat.settleWriteApproval('cancel')}
                          />
                        ) : (
                          <WriteApprovalCard
                            card={message.writeApproval}
                            busy={chat.isStreaming}
                            onApprove={() => chat.settleWriteApproval('approve')}
                            onDecline={() => chat.settleWriteApproval('cancel')}
                          />
                        )
                      ) : (
                        message.awaiting &&
                        !message.taProposal && (
                          <div
                            role="status"
                            className="mt-2 rounded-md border border-warm-border bg-paper-2 px-2 py-1 font-mono text-[10px] text-ink-2"
                          >
                            {awaitingLabel(message.awaiting)}
                          </div>
                        )
                      )}
                    </div>
                  </article>
                ))}
                <div ref={scrollAnchorRef} />
              </div>
            )}
          </div>
        </div>
      )}

      {view === 'chat' && (
        <form
          className="shrink-0 border-t border-warm-border bg-paper p-3"
          onSubmit={(event) => {
            event.preventDefault();
            chat.submit(chat.draft);
          }}
        >
          <div className="rounded-lg border border-warm-border bg-warm-bg focus-within:border-coral focus-within:ring-[3px] focus-within:ring-coral/15">
            <textarea
              aria-label="Message"
              value={chat.draft}
              onChange={(event) => chat.setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  chat.submit(chat.draft);
                }
              }}
              disabled={!chat.tenantId || chat.isStreaming}
              placeholder={chat.tenantId ? 'Ask about this page...' : 'Select a workspace first'}
              rows={2}
              className="block w-full resize-none border-0 bg-transparent px-3 py-2 text-[13px] leading-relaxed outline-none placeholder:text-ink-3 disabled:opacity-60"
            />
            <div className="flex items-center justify-between gap-2 px-2 pb-2">
              <div className="flex min-w-0 items-center gap-2">
                <ChatAccessModeSelect
                  mode={chat.chatAccessMode}
                  onChange={chat.setChatAccessMode}
                  disabled={!chat.tenantId}
                />
                <span
                  className={cn(
                    'size-1.5 rounded-full',
                    chat.isStreaming ? 'animate-pulse bg-teal' : 'bg-ink-3',
                  )}
                  aria-hidden="true"
                />
                <span className="truncate font-mono text-[10px] uppercase tracking-wider text-ink-3">
                  {showBanner ? '' : chat.status || (chat.isStreaming ? 'Streaming' : 'Ready')}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {chat.isStreaming && (
                  <button
                    type="button"
                    onClick={chat.stop}
                    title="Stop"
                    aria-label="Stop"
                    className="grid size-8 place-items-center rounded-full border border-warm-border bg-paper text-ink-2 transition duration-150 hover:border-ink-3 hover:text-ink active:scale-95 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-coral/20"
                  >
                    <Square className="size-3" />
                  </button>
                )}
                <button
                  type="submit"
                  disabled={!chat.draft.trim() || !chat.tenantId || chat.isStreaming}
                  title="Send"
                  className="grid size-8 place-items-center rounded-full bg-primary text-primary-foreground transition duration-150 hover:bg-primary/90 enabled:active:scale-95 disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-coral/20"
                >
                  <Send className="size-4" />
                </button>
              </div>
            </div>
          </div>
        </form>
      )}
    </div>
  );
}
